import { createElement, memo, useCallback, useMemo } from "react";
import { SubCapabilityKey, useApplicationCapabilitiesAllowed } from "../../infra/application/capabilities";
import { DEFAULT_CHAT_LANGUAGE, FetchStatus, useUserInfo, useUserSettings } from "../user";
import buildDiscoverChatbotProfileOverlayViewProps from "./buildDiscoverChatbotProfileOverlayViewProps";
import useChatbotProfile from "./useChatbotProfile";
import useDiscoverChatbotProfileOverlayLabels from "./useDiscoverChatbotProfileOverlayLabels";
import useOptimisticFavorite, { readPendingFavorite } from "./useOptimisticFavorite";
const EMPTY_LIKED_BOTS = [];
/** The View fires argument-less callbacks; the host receives the id only while it is known. */
function useSelectedIdCallback(id, callback) {
    return useCallback(() => {
        if (id) {
            callback(id);
        }
    }, [id, callback]);
}
function useSelectedOptimisticFavorite({ characterId, likedBots, onFavorite, }) {
    const { pendingFavorites, toggleFavorite } = useOptimisticFavorite({ likedBots, onFavorite });
    const isOptimisticFavorite = readPendingFavorite({ pendingFavorites, characterId });
    const handleFavorite = useSelectedIdCallback(characterId, toggleFavorite);
    return {
        isOptimisticFavorite,
        isFavoritePending: isOptimisticFavorite !== null,
        handleFavorite,
    };
}
/** Logical half of the overlay: loads and projects the profile; the injected View owns layout. */
const DiscoverChatbotProfileOverlay = ({ isOpen, characterId, preview, onClose, onChatNow, onFavorite, onShare, onCreatorPress, onLorebookPress, onBlockCreator, onReport, onLessLikeThis, isLorebookIndicatorEnabled, view, }) => {
    const labels = useDiscoverChatbotProfileOverlayLabels();
    const { isAuthenticated, isAuthLoaded, onlyUserId } = useUserInfo();
    const { blurNSFW, likedBots, userChatLanguage, areUserSettingsLoaded, areUserSettingLoading } = useUserSettings();
    const isShareAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.CharacterShareLink);
    const isViewerAuthenticated = Boolean(isAuthenticated);
    const viewerLikedBots = isViewerAuthenticated && likedBots ? likedBots : EMPTY_LIKED_BOTS;
    const isUserSettingsRequestSettled = Boolean(areUserSettingsLoaded) || areUserSettingLoading === FetchStatus.failed;
    const isViewerContextReady = Boolean(isAuthLoaded) && (!isViewerAuthenticated || isUserSettingsRequestSettled);
    const requestLanguage = userChatLanguage !== DEFAULT_CHAT_LANGUAGE ? userChatLanguage : undefined;
    const profileViewModel = useChatbotProfile({ characterId, language: requestLanguage, isEnabled: isOpen && isViewerContextReady });
    const loadedCharacter = profileViewModel.status === "ready" ? profileViewModel.character : null;
    const handleChatNow = useSelectedIdCallback(characterId, onChatNow);
    const { isOptimisticFavorite, isFavoritePending, handleFavorite } = useSelectedOptimisticFavorite({
        characterId,
        likedBots: viewerLikedBots,
        onFavorite,
    });
    const handleShare = useSelectedIdCallback(characterId, onShare);
    const handleReport = useSelectedIdCallback(characterId, onReport);
    const handleLessLikeThis = useSelectedIdCallback(characterId, onLessLikeThis);
    const handleCreatorPress = useSelectedIdCallback(loadedCharacter?.creator_username, onCreatorPress);
    const handleBlockCreator = useSelectedIdCallback(loadedCharacter?.creator_user_id, onBlockCreator);
    const handleRetry = useCallback(() => {
        if (profileViewModel.status === "error") {
            profileViewModel.retry();
        }
    }, [profileViewModel]);
    const viewProps = useMemo(() => buildDiscoverChatbotProfileOverlayViewProps({
        isOpen,
        characterId,
        preview,
        profileViewModel,
        language: userChatLanguage,
        onlyUserId,
        blurNSFW: Boolean(blurNSFW),
        likedBots: viewerLikedBots,
        optimisticFavorite: isOptimisticFavorite,
        isFavoritePending,
        isShareAllowed,
        isLorebookIndicatorEnabled,
        labels,
        callbacks: {
            onClose,
            onChatNow: handleChatNow,
            onFavorite: handleFavorite,
            onShare: handleShare,
            onCreatorPress: handleCreatorPress,
            onLorebookPress,
            onBlockCreator: handleBlockCreator,
            onReport: handleReport,
            onLessLikeThis: handleLessLikeThis,
            onRetry: handleRetry,
        },
    }), [
        isOpen,
        characterId,
        preview,
        profileViewModel,
        userChatLanguage,
        onlyUserId,
        blurNSFW,
        viewerLikedBots,
        isOptimisticFavorite,
        isFavoritePending,
        isShareAllowed,
        isLorebookIndicatorEnabled,
        labels,
        onClose,
        handleChatNow,
        handleFavorite,
        handleShare,
        handleCreatorPress,
        onLorebookPress,
        handleBlockCreator,
        handleReport,
        handleLessLikeThis,
        handleRetry,
    ]);
    return createElement(view, viewProps);
};
export default memo(DiscoverChatbotProfileOverlay);
